import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Recopilación de Información',
    text: 'Recopilamos información personal limitada cuando realizas una compra, incluyendo tu nombre y dirección de correo electrónico. Esta información se utiliza exclusivamente para procesar tu pedido y entregarte el producto digital adquirido.'
  }, {
    heading: 'Uso de la Información',
    text: 'Tu información personal no será vendida, intercambiada ni transferida a terceros sin tu consentimiento, excepto cuando sea necesario para completar la transacción (por ejemplo, procesadores de pago).'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio.'
  }, {
    heading: 'Seguridad',
    text: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información personal durante las transacciones y el almacenamiento de datos.'
  }, {
    heading: 'Cambios a Esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    text: 'Al completar tu compra, recibirás acceso inmediato al manual digital "Prompt Engineering Real-Life" en formato PDF. Este es un producto digital no reembolsable una vez entregado.'
  }, {
    heading: 'Licencia de Uso',
    text: 'La compra te otorga una licencia personal e intransferible para usar el contenido del manual. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido del manual, incluyendo textos, frameworks, metodologías y ejemplos, está protegido por derechos de autor. El uso del método C-R-T-R para fines personales y profesionales está permitido.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'Los resultados obtenidos al aplicar las técnicas del manual pueden variar según el usuario, el modelo de IA utilizado y el contexto específico. No garantizamos resultados específicos.'
  }, {
    heading: 'Jurisdicción',
    text: 'Estos términos se rigen por las leyes aplicables en la jurisdicción del vendedor. Cualquier disputa será resuelta mediante los mecanismos legales correspondientes.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-8 px-4 border-t border-white/5" data-id="element-35">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-36">
          <span className="text-sm text-gray-500" data-id="element-37">
            © {new Date().getFullYear()} Prompt Engineering Real-Life
          </span>
          <div className="flex items-center gap-6" data-id="element-38">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-500 hover:text-cyan-accent transition-colors" data-id="element-39">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-40">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-500 hover:text-cyan-accent transition-colors" data-id="element-41">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-42">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-43">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-44" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto glass-card rounded-2xl border-cyan-accent/30" data-id="element-45">
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between p-6 pb-4 bg-navy-800/90 backdrop-blur-md border-b border-white/5 rounded-t-2xl" data-id="element-46">
                <h3 className="text-xl font-bold text-white" data-id="element-47">
                  {content.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-48">
                  <XIcon className="w-5 h-5" data-id="element-49" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6" data-id="element-50">
                {content.sections.map((section, index) => <div key={index} data-id="element-51">
                    <h4 className="text-lg font-semibold text-white mb-2" data-id="element-52">
                      {section.heading}
                    </h4>
                    <p className="text-gray-400 leading-relaxed" data-id="element-53">
                      {section.text}
                    </p>
                  </div>)}
                <p className="text-xs text-gray-600 pt-4 border-t border-white/5" data-id="element-54">
                  Última actualización: Marzo 2026
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}